import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectCurrentUser } from "../../../app/features/auth/authSlice";
import { clearProject } from "../../../app/features/project/projectSlice";
import { User } from "../../../types/interfaces";
import { useProjectData } from "./useProjectData";
import { useProjectTable } from "./useProjectTable";
import { useProjectActions } from "./useProjectActions";
import useImportProject from "./useImportProject";
import { SelectProjectHookReturn } from "./types";

export const useSelectProject = (): SelectProjectHookReturn => {
    const user: User | null = useAppSelector(selectCurrentUser);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(clearProject());
    }, [dispatch]);

    const {
        tableData,
        isError,
        isLoading,
        projectsCount,
        handleNext,
        handlePrev,
        handlePageClick,
        refetchProjects,
        ITEMS_PER_PAGE
    } = useProjectData();

    const actions = useProjectActions(user?.id ?? 0, refetchProjects);

    const importState = useImportProject(refetchProjects);

    const { columns } = useProjectTable({
        onDuplicate: actions.handleDuplicateProject,
        onRemove: actions.showRemoveModal,
        onDelete: actions.showDeleteModal,
        onEdit: actions.handleEditProject,
        onFinalize: actions.showFinalizeModal,
        onEditTags: actions.showEditTagsModal,
        onRestore: actions.handleRestore,
        onExport: actions.handleExportProject
    });

    return {
        // Table state
        user,
        tableData,
        columns,
        isError,
        isLoading,
        projectsCount,
        ITEMS_PER_PAGE,

        // Actions
        handleNewProject: actions.handleNewProject,
        handleNext,
        handlePrev,
        handlePageClick,
        refetchProjects,

        // Grouped states
        delete: {
            isDeleteDialogOpen: actions.isDeleteDialogOpen,
            handleDelete: actions.handleDelete,
            handleCancel: actions.handleCancel,
            isLoadingDelete: actions.isLoadingDelete,
            deleteProjectErrorMsg: actions.deleteProjectErrorMsg,
            isDeleteDialogErrorMessageOpen: actions.isDeleteDialogErrorMessageOpen,
            closeDeleteDialogErrorMessage: actions.closeDeleteDialogErrorMessage,
        },
        remove: {
            isRemoveDialogOpen: actions.isRemoveDialogOpen,
            handleRemove: actions.handleRemove,
            handleCancelRemove: actions.handleCancelRemove,
            removeMeFromProjectErrorMsg: actions.removeMeFromProjectErrorMsg,
            isRemoveMeFromProjectLoading: actions.isRemoveMeFromProjectLoading,
            isRemoveMeFromProjectErrorMessageOpen: actions.isRemoveMeFromProjectErrorMessageOpen,
            closeRemoveMeFromProjectErrorMessage: actions.closeRemoveMeFromProjectErrorMessage,
        },
        finalize: {
            isFinalizeDialogOpen: actions.isFinalizeDialogOpen,
            handleCancelFinalize: actions.handleCancelFinalize,
            handleFinalize: actions.handleFinalize,
            isLoadingFinalize: actions.isLoadingFinalize,
            finalizeProjectErrorMsg: actions.finalizeProjectErrorMsg,
            isFinalizeDialogErrorMessageOpen: actions.isFinalizeDialogErrorMessageOpen,
            closeFinalizeProjectErrorMessage: actions.closeFinalizeProjectErrorMessage,
        },
        tags: {
            isEditTagsModalOpen: actions.isEditTagsModalOpen,
            closeEditTagsModal: actions.closeEditTagsModal,
            projectToEditTags: actions.projectToEditTags,
            handleSubmitTags: actions.handleSubmitTags,
            isEditTagsDialogErrorMessageOpen: actions.isEditTagsDialogErrorMessageOpen,
            closeEditTagsDialogErrorMessage: actions.closeEditTagsDialogErrorMessage,
            editTagsErrorMsg: actions.editTagsErrorMsg,
        },
        copy: {
            isCopyProjectErrorMessageOpen: actions.isCopyProjectErrorMessageOpen,
            copyProjectErrorMsg: actions.copyProjectErrorMsg,
            closeCopyProjectErrorMessage: actions.closeCopyProjectErrorMessage,
            isLoadingCopy: actions.isLoadingCopy,
            isDuplicateWarningOpen: actions.isDuplicateWarningOpen,
            handleConfirmDuplicate: actions.handleConfirmDuplicate,
            handleCancelDuplicate: actions.handleCancelDuplicate,
        },
        export: {
            isExportWarningOpen: actions.isExportWarningOpen,
            handleConfirmExport: actions.handleConfirmExport,
            handleCancelExport: actions.handleCancelExport,
        },
        import: {
            handleImportClick: importState.handleImportClick,
            isLoadingImport: importState.isLoadingImport,
            importErrorMsg: importState.importErrorMsg,
            isImportErrorMessageOpen: importState.isImportErrorMessageOpen,
            closeImportErrorMessage: importState.closeImportErrorMessage,
            isImportExistsDialogOpen: importState.isImportExistsDialogOpen,
            existingProject: importState.existingProject,
            handleImportForceCopy: importState.handleImportForceCopy,
            handleCancelImportExists: importState.handleCancelImportExists,
        }
    };
}; 